import { ResponsiveTable, useTranslation } from '@hbs-us/ui';

import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { InvoicePayment } from '../contractsMirror.js';

/**
 * Read-only payments table on the invoice detail (legacy `InvoiceDetailPage`
 * Payments card). A real semantic `<table>` — Date · Method · Reference · Amount
 * · Action — with `<th scope="col">` headers, the Amount column right-aligned in
 * mono, and a readable empty message.
 *
 * The per-row delete control is rendered by the parent (it owns the destructive
 * `ConfirmDialog` + the delete handler). Here each row exposes a token-styled
 * delete trigger via the `renderAction` render-prop so the dialog state lives in
 * the view. Every color is a locked token.
 *
 * `ResponsiveTable` reflows the rows to label:value cards below `md`; `md`+ is unchanged.
 */
export function PaymentsTable({
  payments,
  fmt,
  renderAction,
}: {
  payments: InvoicePayment[];
  fmt: MoneyFormatters;
  renderAction: (payment: InvoicePayment) => React.ReactNode;
}) {
  const { t } = useTranslation();

  if (payments.length === 0) {
    return <p className="mt-2 text-muted-foreground text-sm">{t('invoices:payments.none')}</p>;
  }

  return (
    <ResponsiveTable className="mt-2">
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('invoices:payments.tableCaption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th
              scope="col"
              className="px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide"
            >
              {t('invoices:payments.colDate')}
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide"
            >
              {t('invoices:payments.colMethod')}
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide"
            >
              {t('invoices:payments.colReference')}
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
            >
              {t('invoices:payments.colAmount')}
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
            >
              {t('invoices:payments.colAction')}
            </th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={payment.id} className="border-border/60 border-b last:border-b-0">
              <td className="whitespace-nowrap px-3 py-2 font-mono text-foreground text-sm">
                {payment.date}
              </td>
              <td className="px-3 py-2 text-foreground text-sm">
                {payment.method || <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 text-foreground text-sm">
                {payment.reference || <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 text-right font-mono font-medium text-foreground text-sm">
                {fmt.formatCurrency(payment.amount || 0)}
              </td>
              <td className="px-3 py-2 text-right">{renderAction(payment)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}
